import React, { FC, useEffect, useState } from 'react';
import { Button, Header, List, ListItem, Placeholder, ScrollView } from '$uikit';
import { useDispatch } from 'react-redux';
import { LoadingStatus } from '$store/reels/interface';
import { Api } from '$api';
import { toastActions } from '$store/toast';
import { ListInfoModel } from '$store/models';
import { useTranslator } from '$hooks';
import { Loader } from '$shared/components';
import { IcRadioButton24, IcRadioButtonEmpty24 } from '$assets';
import { goTo } from '$navigation/helper';
import { LISTS } from '$shared/constants/pages';
import { useRoute } from 'react-router5';
import { listsActions } from '$store/lists';

export const ListAddMember: FC<{ memberId: number }> = ({ memberId }) => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { route } = useRoute();
  const refresh = route.params.refresh;
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>('loading');
  const [info, setInfo] = useState<null | {
    lists: ListInfoModel[];
    added: { [index: number]: boolean };
  }>(null);

  const handleLoad = () => {
    setLoadingStatus('loading');
    Api.get<
      any,
      {
        lists: ListInfoModel[];
        added: { [index: number]: boolean };
      }
    >(`/lists/member/${memberId}/custom`)
      .then((resp) => {
        setInfo(resp);
        setLoadingStatus('');
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
        setLoadingStatus('failed');
      });
  };

  useEffect(() => {
    handleLoad();
  }, [memberId]);

  const handleToggle = (listId: number) => {
    dispatch(toastActions.setToastLoading());
    const isAdded = info?.added[listId];
    Api.post(
      isAdded
        ? `/lists/member/${memberId}/custom/${listId}/del`
        : `/lists/member/${memberId}/custom/${listId}/add`,
    )
      .then(() => {
        setInfo((prev) =>
          !prev
            ? null
            : {
                ...prev,
                added: {
                  ...(prev?.added ?? {}),
                  [listId]: !isAdded,
                },
              },
        );
        dispatch(toastActions.setToastSuccess());

        if (refresh === `custom${listId}`) {
          dispatch(listsActions.loadListData(refresh));
        }
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
      });
  };

  function renderContent() {
    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          centered
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={handleLoad}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (!info || loadingStatus === 'loading') {
      return <Loader isWrapped />;
    }

    if (info.lists.length === 0) {
      return (
        <Placeholder
          centered
          title={t('member_lists_empty_title')}
          actions={
            <Button
              onClick={() => {
                goTo(LISTS);
              }}
            >
              {t('member_lists_empty_btn')}
            </Button>
          }
        >
          {t('member_lists_empty_text')}
        </Placeholder>
      );
    }

    return (
      <div style={{ padding: '0 16px 16px' }}>
        <List>
          {info.lists.map((list) => {
            const listId = +list.listId.substring(6);
            return (
              <ListItem
                key={list.listId}
                icon={info.added[listId] ? <IcRadioButton24 /> : <IcRadioButtonEmpty24 />}
                onClick={() => {
                  handleToggle(listId);
                }}
              >
                {list.title}
              </ListItem>
            );
          })}
        </List>
      </div>
    );
  }

  return (
    <>
      <Header showCloseButton>{t('lists_title')}</Header>
      <ScrollView>{renderContent()}</ScrollView>
    </>
  );
};
